import React, { useEffect } from 'react';
import ChannelService from '../api/ChannelService';
import HFbutton from '../components/common/input/HFbutton';
import { useAppDispatch } from '../store/hook';
import { setContentInfo } from '../store/features/takeclassSice';
import { getContentsDatas } from '../api/utils/lms/lmsApi';
import { getContentView } from '../api/utils/api/learningApi';
import { getContentsDatas_light } from '../api/utils/api/lightApi';
import { Spin } from 'antd';

// FIXME: 공통으로 쓰고 있는 타입인데, 분리 필요
type ContentType = 'Vimeo' | 'HTML' | 'Jebspl' | 'todoView' | '';

interface ContentsData {
  contentsUrl: string;
  contentsType: ContentType;
}

// 메세지 이벤트 타입
interface MessageEvent {
  type: 'contentsView' | 'todoView' | 'learningView' | 'closeEvent';
  contents: {
    classId?: string;
    contentId?: string;
    isLight?: boolean;
    url?: string;
    learningId?: string;
    lessonId?: string;
  };
}

const ContentsView = () => {
  const dispatch = useAppDispatch();

  // 1. 세션 스토리지에서 이전 데이터 확인
  const [contentsData, setContentsData] = React.useState<
    ContentsData | undefined
  >(() => {
    try {
      const savedData = sessionStorage.getItem('contentsData');
      return savedData ? JSON.parse(savedData) : undefined;
    } catch (error) {
      console.error('콘텐츠 데이터 로드 실패:', error);
      return undefined;
    }
  });

  const [posibleFullScreen, setPosibleFullScreen] = React.useState(false);
  const [isFullScreen, setIsFullScreen] = React.useState(false);
  const doc = document.documentElement;

  // 데이터 저장 함수
  const saveContentsData = (data: ContentsData) => {
    try {
      sessionStorage.setItem('contentsData', JSON.stringify(data));
      setContentsData(data);
    } catch (error) {
      console.error('콘텐츠 데이터 저장 실패:', error);
    }
  };

  // URL 생성 함수 객체
  const getContentUrl: Record<ContentType, (contents: any) => string> = {
    Vimeo: (contents: any) => {
      // 비메오의 경우 필요
      // dispatch(setContentInfo(contents));
      return `/vimeo/${contents.id}`;
    },
    HTML: (contents: any) => `https://content.jebs.kr/${contents.resourcePath}`,
    Jebspl: (contents: any) => contents.jebsplUrl,
    '': () => '',
    todoView: () => '',
  };

  const getTakeclassUrl = (contents: any) => {
    return getContentUrl[contents.type as ContentType]?.(contents);
  };

  // API 호출 함수들
  const contentHandlers = {
    // type : 'contentsView' 인 경우 url 알아내서 saveContentsData 실행
    async getTakeclassData(
      classId: string,
      contentId: string,
      isLight?: boolean,
    ) {
      const contents = isLight
        ? await getContentsDatas_light(classId, contentId)
        : await getContentsDatas(classId, contentId);

      if (contents.type === 'Vimeo') {
        // VimeoContentView를 열기 위해 사용
        dispatch(setContentInfo(contents));
      }

      // contentsType : HTML, Vimeo, Jebspl(Acts수업자료)
      saveContentsData({
        contentsUrl: getTakeclassUrl(contents),
        contentsType: contents.type as ContentType,
      });
    },

    /**
     *
     * @param learningId
     * @param lessonId
     * interface LearningLessonContentType  {
     * id: string;
     * name: string;
     * division: string;
     * type: 'Vimeo' | string;
     * vimeo_key: string;
     * running_time: string;
     * created_at: Date;
     * updated_at: Date;
     * path: string;
     * exe_file: { resource_path: string };
     * }
     */
    async getLearningView(learningId: string, lessonId: string) {
      const data = await getContentView(learningId, lessonId);

      let url;
      switch (data.type) {
        case 'Vimeo':
          // VimeoContentView를 열기 위해 사용
          dispatch(
            setContentInfo({
              id: data.id,
              type: 'Vimeo',
              vimeoKey: data.vimeo_key,
            }),
          );
          url = getContentUrl.Vimeo({ ...data, type: 'Vimeo' });
          break;
        default:
          url = `https://content.jebs.kr/${data.exe_file.resource_path}`;
      }

      saveContentsData({
        contentsUrl: url,
        contentsType: data.type as ContentType,
      });
    },
  };

  // 전체화면 관련 함수들
  const fullscreenHandlers = {
    onChange: () => setIsFullScreen(!!document.fullscreenElement),
    enter: () => doc.requestFullscreen(),
  };

  // 메시지 이벤트 핸들러
  const handleMessage = (event: { data: MessageEvent }) => {
    const { type, contents } = event.data;

    switch (type) {
      case 'contentsView':
        contentHandlers.getTakeclassData(
          contents.classId!,
          contents.contentId!,
          contents.isLight,
        );
        break;
      // 학생할일 (젭스온, 젭스플 상관 없이)
      case 'todoView':
        saveContentsData({
          contentsUrl: contents.url!,
          contentsType: 'todoView',
        });
        break;
      //교사대학
      case 'learningView':
        contentHandlers.getLearningView(
          contents.learningId!,
          contents.lessonId!,
        );
        break;
      case 'closeEvent':
        if (window.opener && !window.opener.closed) {
          const parentUrl = window.opener.location.href;
          if (parentUrl.match(/student\/(class|homework)\/[^\/]+/)) {
            window.opener.location.reload();
          }
        }
        // 창 닫기
        window.close();
        break;
    }
  };

  // 2. 컴포넌트 마운트 시 초기 설정
  useEffect(() => {
    // 2-1. 전체화면 지원 확인
    if (doc.requestFullscreen) setPosibleFullScreen(true);

    // 2-2. 채널톡 버튼 숨김
    ChannelService.hideChannelButton();

    // 2-3. 이벤트 리스너 등록
    // 2-3-1. 전체화면 이벤트 리스너 추가
    document.addEventListener('fullscreenchange', fullscreenHandlers.onChange);
    // 2-3-2. 메세지 이벤트 리스너 추가
    window.addEventListener('message', handleMessage);

    // 클린업 : 컴포넌트 언마운트 시 채널 버튼 보이기
    return () => {
      // 1. 채널톡 버튼 다시 표시
      ChannelService.showChannelButton();
      // 2. 이벤트 리스너 제거
      document.removeEventListener(
        'fullscreenchange',
        fullscreenHandlers.onChange,
      );
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  // 로딩 상태
  if (contentsData === undefined) {
    return (
      <div className="flex h-screen w-screen items-center justify-center">
        <Spin size="large" tip="콘텐츠 로딩중..." />
      </div>
    );
  }
  // Vimeo가 아닐 때만 전체화면 버튼 표시
  const visibleFullScreenBtn =
    posibleFullScreen && !isFullScreen && contentsData.contentsType !== 'Vimeo';

  return (
    <div className="h-screen w-screen">
      {!contentsData.contentsUrl ? (
        <div>비정상적인 접근입니다.</div>
      ) : (
        <>
          <iframe
            src={contentsData.contentsUrl}
            width={'100%'}
            height={'100%'}
            allowFullScreen
            allow="microphone"
            title="contents-view"
          />
          {visibleFullScreenBtn && (
            <div className="fixed right-1 top-1 h-10 w-20">
              <HFbutton height={36} onClick={fullscreenHandlers.enter}>
                전체화면
              </HFbutton>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ContentsView;
